import { observer } from "mobx-react-lite";
import { useState, type FormEvent } from "react";
import styled from "styled-components";

import type { SetupRequest } from "../../desktop.ts";
import { useStores } from "../../stores/RootStore.ts";
import { LlmFields } from "../ui/LlmFields.tsx";
import { Button, Card, ErrorText, Input, Label, Muted, Page, Row, Title } from "../ui/primitives.tsx";
import { ProgressView } from "../ui/ProgressView.tsx";

const Log = styled.pre`
    margin: 0;
    padding: 12px;
    border-radius: 8px;
    background: var(--background);
    font-size: 12px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    max-height: 40vh;
    overflow-y: auto;
`;

const Steps = styled.ol`
    margin: 0;
    padding-left: 20px;
`;

type Form = Omit<SetupRequest, "llm" | "port"> & { repeatPassword: string; llmUrl: string; llmKey: string; llmModel: string };

/** The shortest mainions password allowed. Anyone can try to log in to your server, so it has to be hard to guess. */
const MIN_PASSWORD_LENGTH = 12;

/** Sets up mainions on a fresh server: its main controller. Only in the desktop app, because it needs SSH. */
export const SetupPage = observer(function SetupPage() {
    const { setup, connection, ui } = useStores();
    const { llmUrl, llmKey, llmModel } = connection.settings;

    const [form, setForm] = useState<Form>({
        host: "",
        user: "root",
        password: "",
        mainionsPassword: "",
        repeatPassword: "",
        domain: "",
        name: "vps1",
        llmUrl,
        llmKey,
        llmModel,
    });

    const passwordsDiffer = form.repeatPassword !== "" && form.repeatPassword !== form.mainionsPassword;

    const field = (name: keyof Form) => ({
        value: form[name] ?? "",
        onChange: (event: { target: { value: string } }) => setForm({ ...form, [name]: event.target.value }),
    });

    function start(event?: FormEvent) {
        event?.preventDefault();
        if (form.mainionsPassword !== form.repeatPassword) return;
        const { llmUrl, llmKey, llmModel, password, domain, host, repeatPassword: _, ...server } = form;
        // "203.0.113.7:2222" means SSH on port 2222.
        const withPort = /^([^:]+):(\d+)$/.exec(host);

        setup.start({
            ...server,
            host: withPort?.[1] ?? host,
            ...(withPort && { port: Number(withPort[2]) }),
            ...(password && { password }),
            ...(domain && { domain }),
            llm: { url: llmUrl, model: llmModel, ...(llmKey && { key: llmKey }) },
        });
    }

    if (setup.status === "idle") {
        return (
            <Page as="form" onSubmit={start}>
                <Card>
                    <Title>Set up mainions on a new server</Title>
                    <Muted>
                        You need a fresh Linux server, like a VPS with Ubuntu or Debian, and its login. An agent installs everything over
                        SSH, locks the server down and checks that it all works. This takes about 10 minutes.
                    </Muted>
                    <Label>
                        Server address
                        <Input {...field("host")} placeholder="203.0.113.7, or 203.0.113.7:2222 for another SSH port" required />
                    </Label>
                    <Label>
                        SSH user
                        <Input {...field("user")} required />
                    </Label>
                    <Label>
                        SSH password
                        <Input {...field("password")} type="password" placeholder="Leave empty to use your SSH keys" />
                    </Label>
                    <Muted>
                        Only used to log in the first time. Afterwards, this app logs in with its own key, and logging in with a password is
                        turned off.
                    </Muted>
                    <Label>
                        Domain name
                        <Input {...field("domain")} placeholder="Leave empty to use <ip>.sslip.io" />
                    </Label>
                    <Label>
                        Controller name
                        <Input {...field("name")} required pattern="[a-z0-9\-]+" title="Lowercase letters, numbers and dashes" />
                    </Label>
                </Card>
                <Card>
                    <Title>Your mainions password</Title>
                    <Muted>
                        Choose the password you'll log in to mainions with, from this app or any browser. It replaces the server's login
                        above, which you won't need anymore.
                    </Muted>
                    <Label>
                        Mainions password
                        <Input
                            {...field("mainionsPassword")}
                            type="password"
                            autoComplete="new-password"
                            minLength={MIN_PASSWORD_LENGTH}
                            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                            required
                        />
                    </Label>
                    <Label>
                        Repeat it
                        <Input {...field("repeatPassword")} type="password" autoComplete="new-password" required />
                    </Label>
                    {passwordsDiffer && <ErrorText>The passwords aren't the same.</ErrorText>}
                </Card>
                <Card>
                    <Title>LLM for the setup agent</Title>
                    <Muted>Your workers will use it too.</Muted>
                    <LlmFields value={form} onChange={(llm) => setForm({ ...form, ...llm })} required />
                </Card>
                <Row>
                    <Button $primary type="submit">
                        Set up
                    </Button>
                    <Button type="button" onClick={() => ui.show({ name: "welcome" })}>
                        Back
                    </Button>
                </Row>
            </Page>
        );
    }

    return (
        <Page>
            <Card>
                <Title>Setting up {form.host}</Title>
                {setup.status === "running" && (
                    <>
                        {setup.progress ? <ProgressView progress={setup.progress} /> : <Muted>Connecting…</Muted>}
                        <Row>
                            <Button $danger onClick={() => setup.cancel()}>
                                Stop
                            </Button>
                        </Row>
                    </>
                )}
                {setup.status === "done" && (
                    <>
                        <p>
                            mainions is running, and this app is logged in. On other devices, open{" "}
                            {connection.settings.url.replace("wss://", "https://")} and log in with your mainions password.
                        </p>
                        <Row>
                            <Button $primary onClick={() => ui.show({ name: "workers" })}>
                                Go to workers
                            </Button>
                        </Row>
                    </>
                )}
                {setup.status === "failed" && (
                    <>
                        <ErrorText>{setup.reason}</ErrorText>
                        <Row>
                            <Button $primary onClick={() => start()}>
                                Try again
                            </Button>
                        </Row>
                    </>
                )}
                {setup.status === "reset" && (
                    <>
                        <ErrorText>The server got into a state the agent can't repair: {setup.reason}</ErrorText>
                        <p>Start over with a fresh server:</p>
                        <Steps>
                            <li>Go to the admin page of your server's provider.</li>
                            <li>Reinstall the server with a fresh Ubuntu or Debian. This erases everything on it.</li>
                            <li>If the provider gives you a new password, fill it in below.</li>
                            <li>Press Try again.</li>
                        </Steps>
                        <Label>
                            SSH password
                            <Input {...field("password")} type="password" placeholder="Leave empty to use your SSH keys" />
                        </Label>
                        <Row>
                            <Button $primary onClick={() => start()}>
                                Try again
                            </Button>
                        </Row>
                    </>
                )}
            </Card>
            <Card>
                <Title as="h2" style={{ fontSize: 16 }}>
                    What the agent did
                </Title>
                <Log>{setup.log.join("\n") || "Nothing yet."}</Log>
            </Card>
        </Page>
    );
});
